import React, { useEffect, useState, useMemo } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    Input,
    Row,
    Col,
    Label,
    Button
} from 'reactstrap';
import axios from 'axios';
import config from '../../config';
import { store } from '../../store';

export default function DelayedOrdersModal({ isOpen, onClose }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const [sponsorFilter, setSponsorFilter] = useState('');
    const [orderIdFilter, setOrderIdFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [partnerFilter, setPartnerFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSponsorFilter('');
            setOrderIdFilter('');
            setStateFilter('');
            setPartnerFilter('');
            setStartDate('');
            setEndDate('');
            return;
        }

        const fetchOrderData = async () => {
            setLoading(true);
            try {
                const state = store.getState();
                const headers = state.user.token ? { authorization: state.user.token } : {};
                const { data } = await axios.get(`${config.pharmacyService}/ops/fetch-delayed-order-list`, { headers });
                setOrders(data?.orders || []);
            } catch (err) {
                alert(err?.response?.data?.message || 'Failed to fetch orders. Showing mock data.');
            } finally {
                setLoading(false);
            }
        };

        fetchOrderData();
    }, [isOpen]);

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const orderDate = new Date(order.placedTime);

            const isAfterStart = startDate ? orderDate >= new Date(startDate) : true;
            const isBeforeEnd = endDate ? orderDate <= new Date(endDate + 'T23:59:59') : true;

            return (
            (sponsorFilter === '' || (order.sponsorName && order.sponsorName.toLowerCase().includes(sponsorFilter.toLowerCase()))) &&
            (orderIdFilter === '' || (order.orderId && order.orderId.toString().includes(orderIdFilter))) &&
            (stateFilter === '' || (order.state && order.state.toLowerCase().includes(stateFilter.toLowerCase()))) &&
            (partnerFilter === '' || (order.partner && order.partner.toLowerCase().includes(partnerFilter.toLowerCase()))) &&
            isAfterStart &&
            isBeforeEnd
            );
        });
    }, [orders, sponsorFilter, orderIdFilter, stateFilter, partnerFilter, startDate, endDate]);

    // 📥 CSV Download Handler
    const handleDownloadCSV = async () => {
        try {
            setIsDownloading(true);
            const appliedFilters = prepareApiFilters();
            const state = store.getState();
            const headers = state.user.token ? { authorization: state.user.token } : {};

            const response = await axios.get(`${config.pharmacyService}/ops/download-delayed-ph-orders-csv`, {
            headers,
            params: appliedFilters,
            responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DelayedOrders_${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || 'Failed to download CSV');
        } finally {
            setIsDownloading(false);
        }
    };


    const prepareApiFilters = () => {
        const filters = {};

        if (sponsorFilter) filters.sponsor = sponsorFilter;
        if (orderIdFilter) filters.orderId = orderIdFilter;
        if (stateFilter) filters.state = stateFilter;
        if (partnerFilter) filters.partner = partnerFilter;
        if (startDate) filters.startDate = startDate;
        if (endDate) filters.endDate = endDate;

        return filters;
    };


    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="xl"
            style={{ maxWidth: '95vw', width: '95vw', height: '90vh' }}
            className="delayed-orders-modal"
        >
            <ModalHeader
                toggle={onClose}
                style={{
                    backgroundColor: 'rgb(234, 241, 249)',
                    color: 'rgb(44, 62, 80)',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    paddingTop: '15px',
                    paddingBottom: '15px'
                }}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: '25px'
                }}>
                    <div style={{ margin: '0 auto' }}>Delayed Orders</div>
                    <div>
                        <span
                            style={{
                                backgroundColor: '#fff',
                                color: '#000',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                            }}
                        >
                            Showing <strong>{filteredOrders.length}</strong> of <strong>{orders.length}</strong> orders
                        </span>
                    </div>
                </div>
            </ModalHeader>

            <ModalBody style={{ height: 'calc(90vh - 56px)', overflowY: 'auto' }}>
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <>
                        {/* 🔍 Filters Row */}
                        <Row className="mb-3" style={{ alignItems: 'end' }}>
                            <Col md={2}>
                                <Label>Sponsor</Label>
                                <Input
                                    type="text"
                                    value={sponsorFilter}
                                    onChange={e => setSponsorFilter(e.target.value)}
                                    placeholder="e.g. Niva Bupa Tele GP"
                                />
                            </Col>
                            <Col md={2}>
                                <Label>Order Id</Label>
                                <Input
                                    type="text"
                                    value={orderIdFilter}
                                    onChange={e => setOrderIdFilter(e.target.value)}
                                    placeholder="e.g. 62891"
                                />
                            </Col>
                            <Col md={2}>
                                <Label>State</Label>
                                <Input
                                    type="text"
                                    value={stateFilter}
                                    onChange={e => setStateFilter(e.target.value)}
                                    placeholder="e.g. Maharashtra"
                                />
                            </Col>
                            <Col md={2}>
                                <Label>Partner</Label>
                                <Input
                                    type="text"
                                    value={partnerFilter}
                                    onChange={e => setPartnerFilter(e.target.value)}
                                    placeholder="e.g. Apollo"
                                />
                            </Col>
                            <Col md={1.5}>
                                <Label>From Date</Label>
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                />
                            </Col>
                            <Col md={1.5}>
                                <Label>To Date</Label>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    min={startDate}
                                />
                            </Col>
                        </Row>
                    
                        <div
                            className="mb-3"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: '-10px'
                            }}
                        >
                            <Button
                                color="primary"
                                onClick={handleDownloadCSV}
                                disabled={isDownloading}
                                style={{
                                    fontWeight: '500',
                                    padding: '6px 20px',
                                    borderRadius: '6px'
                                }}
                                >
                                {isDownloading ? 'Downloading...' : 'Download CSV'}
                            </Button>
                        </div>
                        {/* Table */}
                        <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
                            <table className="table table-bordered table-striped table-hover">
                                <thead
                                    className="thead-dark"
                                    style={{
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: '#343a40',
                                        color: '#fff'
                                    }}
                                >
                                    <tr>
                                        <th>Order ID</th>
                                        <th>City</th>
                                        <th>State</th>
                                        <th>Tier</th>
                                        <th>Placed Time</th>
                                        <th>Status</th>
                                        <th>Delay Time</th>
                                        <th>Partner</th>
                                        <th>Sponsor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map((order, index) => (
                                        <tr key={index}>
                                            <td>{order.orderId}</td>
                                            <td>{order.city}</td>
                                            <td>{order.state}</td>
                                            <td>{order.tier}</td>
                                            <td>{order.placedTime}</td>
                                            <td>{order.status}</td>
                                            <td>{order.delayTime}</td>
                                            <td>{order.partner}</td>
                                            <td>{order.sponsorName}</td>
                                        </tr>
                                    ))}
                                    {filteredOrders.length === 0 && (
                                        <tr>
                                            <td colSpan="9" className="text-center">No matching records found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
}
